import type { AudioCue, GameState, MusicMode } from "../game/domain/types";

type MusicalCue = { notes: number[]; duration: number; wave?: OscillatorType };
type ThemeNote = {
  frequency: number;
  beat: number;
  beats: number;
  level: number;
  pan: number;
};
type MusicProfile = {
  track: "menu" | "game" | null;
  rate: number;
  volume: number;
};

const MUSIC_PROFILES: Record<MusicMode, MusicProfile> = {
  menu: { track: "menu", rate: 1, volume: 0.24 },
  gameplay: { track: "game", rate: 1, volume: 0.2 },
  danger: { track: "game", rate: 1.12, volume: 0.2 },
  victory: { track: null, rate: 1, volume: 0 },
  defeat: { track: "menu", rate: 0.82, volume: 0.16 },
};

const VICTORY_THEME_BPM = 108;
const VICTORY_THEME_BEATS = 16;
const VICTORY_THEME_VOLUME = 0.15;

// Four calm, ascending bars with a short cadence. Every note has a defined
// release, so the theme feels musical without reintroducing a sustained pad.
const VICTORY_THEME_LEAD: readonly ThemeNote[] = [
  { frequency: 523.25, beat: 0, beats: 0.8, level: 0.42, pan: -0.12 },
  { frequency: 659.25, beat: 1, beats: 0.58, level: 0.38, pan: 0.08 },
  { frequency: 783.99, beat: 2, beats: 0.9, level: 0.42, pan: 0.14 },
  { frequency: 659.25, beat: 3.25, beats: 0.55, level: 0.34, pan: -0.08 },
  { frequency: 587.33, beat: 4, beats: 0.75, level: 0.38, pan: -0.14 },
  { frequency: 698.46, beat: 5, beats: 0.58, level: 0.36, pan: 0.06 },
  { frequency: 880, beat: 6, beats: 0.95, level: 0.42, pan: 0.14 },
  { frequency: 783.99, beat: 7.3, beats: 0.5, level: 0.34, pan: -0.06 },
  { frequency: 659.25, beat: 8, beats: 0.72, level: 0.38, pan: -0.12 },
  { frequency: 783.99, beat: 9, beats: 0.58, level: 0.38, pan: 0.08 },
  { frequency: 1046.5, beat: 10, beats: 1.15, level: 0.45, pan: 0.14 },
  { frequency: 987.77, beat: 11.55, beats: 0.45, level: 0.32, pan: -0.08 },
  { frequency: 880, beat: 12, beats: 0.68, level: 0.38, pan: -0.14 },
  { frequency: 783.99, beat: 13, beats: 0.58, level: 0.36, pan: 0.06 },
  { frequency: 659.25, beat: 14, beats: 0.55, level: 0.34, pan: 0.12 },
  { frequency: 523.25, beat: 15, beats: 0.7, level: 0.4, pan: 0 },
] as const;

const VICTORY_THEME_BASS: readonly ThemeNote[] = [
  { frequency: 130.81, beat: 0, beats: 0.75, level: 0.2, pan: -0.05 },
  { frequency: 196, beat: 2, beats: 0.65, level: 0.15, pan: 0.05 },
  { frequency: 110, beat: 4, beats: 0.75, level: 0.19, pan: -0.05 },
  { frequency: 164.81, beat: 6, beats: 0.65, level: 0.15, pan: 0.05 },
  { frequency: 87.31, beat: 8, beats: 0.75, level: 0.19, pan: -0.05 },
  { frequency: 130.81, beat: 10, beats: 0.65, level: 0.15, pan: 0.05 },
  { frequency: 98, beat: 12, beats: 0.75, level: 0.19, pan: -0.05 },
  { frequency: 146.83, beat: 14, beats: 0.65, level: 0.15, pan: 0.05 },
] as const;

const MUSICAL_CUES: Partial<Record<AudioCue, MusicalCue>> = {
  target: { notes: [420, 560], duration: 0.09, wave: "sine" },
  "spawn-threat": { notes: [300, 190], duration: 0.12, wave: "sawtooth" },
  "spawn-opportunity": { notes: [520, 680], duration: 0.1, wave: "triangle" },
  "spawn-recovery": { notes: [523.25, 659.25], duration: 0.13, wave: "sine" },
  resolved: {
    notes: [523.25, 659.25, 783.99],
    duration: 0.12,
    wave: "triangle",
  },
  shield: { notes: [392, 523.25], duration: 0.16, wave: "sine" },
  heal: { notes: [523.25, 659.25], duration: 0.18, wave: "sine" },
  bonus: { notes: [659.25, 783.99], duration: 0.16, wave: "triangle" },
  combo: { notes: [440, 554.37, 659.25], duration: 0.14, wave: "triangle" },
  damage: { notes: [130.81, 98], duration: 0.2, wave: "sawtooth" },
  "shield-break": { notes: [330, 165], duration: 0.16, wave: "square" },
  defeat: { notes: [392, 311.13, 261.63], duration: 0.42, wave: "sine" },
};

export class AudioManager {
  private context: AudioContext | null = null;
  private sfxGain: GainNode | null = null;
  private compressor: DynamicsCompressorNode | null = null;
  private readonly menuMusic = new Audio();
  private readonly gameMusic = new Audio();
  private readonly musicObjectUrls: string[] = [];
  private readonly fadeVersions = new WeakMap<HTMLAudioElement, number>();
  private readonly rateVersions = new WeakMap<HTMLAudioElement, number>();
  private musicPrepared = false;
  private muted = false;
  private unlocked = false;
  private paused = false;
  private lifecycleSuspended = false;
  private currentMode: MusicMode | null = null;
  private victoryThemeBuffer: AudioBuffer | null = null;
  private victoryThemeSource: AudioBufferSourceNode | null = null;
  private victoryThemeGain: GainNode | null = null;
  private voices = 0;
  private lastTypoAt = 0;

  constructor() {
    [this.menuMusic, this.gameMusic].forEach((audio) => {
      audio.loop = true;
      audio.preload = "auto";
      audio.volume = 0;
      audio.preservesPitch = true;
    });
  }

  async unlock(): Promise<void> {
    if (!this.context) {
      const AudioContextConstructor =
        window.AudioContext ?? window.webkitAudioContext;
      if (AudioContextConstructor) {
        this.context = new AudioContextConstructor();
        this.sfxGain = this.context.createGain();
        this.sfxGain.gain.value = 0.28;
        this.compressor = this.context.createDynamicsCompressor();
        this.sfxGain.connect(this.compressor).connect(this.context.destination);
      }
    }
    if (!this.lifecycleSuspended) await this.context?.resume();
    this.unlocked = true;
    await this.prepareMusic();
    this.setMusicMode(this.currentMode ?? "menu", 500, true);
  }

  playMenu(): void {
    this.paused = false;
    this.setMusicMode("menu", 500);
  }

  playGame(): void {
    this.paused = false;
    this.setMusicMode("gameplay", 500);
  }

  showResult(won: boolean): void {
    this.paused = false;
    this.setMusicMode(won ? "victory" : "defeat", 600);
  }

  syncGameplayState(
    state: Pick<GameState, "status" | "hearts" | "shieldActive">,
  ): void {
    if (state.status !== "playing") return;
    this.setMusicMode(
      state.hearts === 1 && !state.shieldActive ? "danger" : "gameplay",
      350,
    );
  }

  setPaused(paused: boolean): void {
    if (this.paused === paused) return;
    this.paused = paused;
    void this.applyMode(250);
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    [this.menuMusic, this.gameMusic].forEach((audio) => {
      audio.muted = muted;
    });
    if (muted) {
      this.cancelAnimations();
      this.menuMusic.volume = 0;
      this.gameMusic.volume = 0;
      if (this.context) void this.context.suspend();
      return;
    }
    if (!this.unlocked || this.lifecycleSuspended) return;
    if (this.context) void this.context.resume();
    void this.applyMode(250);
  }

  isMuted(): boolean {
    return this.muted;
  }

  async setLifecycleSuspended(suspended: boolean): Promise<boolean> {
    if (suspended) {
      if (this.lifecycleSuspended) return true;
      this.lifecycleSuspended = true;
      this.cancelAnimations();
      this.menuMusic.pause();
      this.gameMusic.pause();
      try {
        await this.context?.suspend();
      } catch {
        // Some mobile browsers can interrupt the context before suspension.
      }
      return true;
    }

    if (!this.lifecycleSuspended) return true;
    if (this.muted || !this.unlocked) {
      this.lifecycleSuspended = false;
      return true;
    }

    try {
      this.lifecycleSuspended = false;
      const mode = this.currentMode;
      const track = mode ? MUSIC_PROFILES[mode].track : null;
      const active =
        track === "menu"
          ? this.menuMusic
          : track === "game"
            ? this.gameMusic
            : null;
      const mediaResume = active
        ? this.safePlay(active)
        : Promise.resolve(true);
      const contextResume = this.context?.resume() ?? Promise.resolve();
      const [mediaStarted] = await Promise.all([mediaResume, contextResume]);
      if (mediaStarted) {
        await this.applyMode(250, true);
        return true;
      }
    } catch {
      // Keep the suspended state so the next trusted interaction can retry.
    }

    this.lifecycleSuspended = true;
    this.menuMusic.pause();
    this.gameMusic.pause();
    try {
      await this.context?.suspend();
    } catch {
      // The context may already be interrupted by the browser.
    }
    return false;
  }

  play(cue: AudioCue, progress = 0): void {
    if (!this.context || !this.sfxGain || this.muted || this.lifecycleSuspended)
      return;
    if (cue === "victory") {
      return;
    }
    if (this.voices >= 6) return;
    if (cue === "typo") {
      const now = performance.now();
      if (now - this.lastTypoAt < 60) return;
      this.lastTypoAt = now;
      this.tone([180], 0.07, "square", 0);
      return;
    }
    if (cue === "correct-key") {
      this.tone([620 + progress * 240], 0.03, "triangle", 0);
      return;
    }
    const musical = MUSICAL_CUES[cue];
    if (musical)
      this.tone(
        musical.notes,
        musical.duration,
        musical.wave ?? "sine",
        cue === "defeat" ? 0.09 : 0,
      );
  }

  getDebugState(): {
    mode: MusicMode | null;
    activeTrack: "menu" | "game" | null;
    paused: boolean;
    rate: number;
    volume: number;
    muted: boolean;
    lifecycleSuspended: boolean;
    musicElementsMuted: boolean;
    musicElementsPaused: boolean;
    victoryThemeActive: boolean;
  } {
    const profile = this.currentMode ? MUSIC_PROFILES[this.currentMode] : null;
    const activeMusic = profile
      ? profile.track === "menu"
        ? this.menuMusic
        : profile.track === "game"
          ? this.gameMusic
          : null
      : null;
    return {
      mode: this.currentMode,
      activeTrack: profile?.track ?? null,
      paused: this.paused,
      rate: activeMusic?.playbackRate ?? 1,
      volume: activeMusic?.volume ?? 0,
      muted: this.muted,
      lifecycleSuspended: this.lifecycleSuspended,
      musicElementsMuted: this.menuMusic.muted && this.gameMusic.muted,
      musicElementsPaused: this.menuMusic.paused && this.gameMusic.paused,
      victoryThemeActive: this.victoryThemeSource !== null,
    };
  }

  dispose(): void {
    this.stopVictoryTheme();
    this.menuMusic.pause();
    this.gameMusic.pause();
    void this.context?.close();
    this.context = null;
    this.musicObjectUrls.forEach((url) => URL.revokeObjectURL(url));
    this.musicObjectUrls.length = 0;
  }

  private setMusicMode(
    mode: MusicMode,
    durationMs: number,
    force = false,
  ): void {
    if (!force && this.currentMode === mode) return;
    if (mode !== "victory") this.stopVictoryTheme();
    this.currentMode = mode;
    void this.applyMode(durationMs);
  }

  private async applyMode(
    durationMs: number,
    activeAlreadyStarted = false,
  ): Promise<boolean> {
    const mode = this.currentMode;
    if (!mode || !this.unlocked || this.muted || this.lifecycleSuspended)
      return true;
    const profile = MUSIC_PROFILES[mode];
    const active =
      profile.track === "menu"
        ? this.menuMusic
        : profile.track === "game"
          ? this.gameMusic
          : null;
    const targetVolume = profile.volume * (this.paused ? 0.35 : 1);
    if (mode === "victory") this.startVictoryTheme();
    if (!active) {
      this.fade(this.menuMusic, 0, durationMs);
      this.fade(this.gameMusic, 0, durationMs);
      return true;
    }
    const inactive =
      active === this.menuMusic ? this.gameMusic : this.menuMusic;
    const started = activeAlreadyStarted
      ? Promise.resolve(true)
      : this.safePlay(active);
    this.fade(inactive, 0, durationMs);
    this.fade(active, targetVolume, durationMs);
    this.animateRate(active, profile.rate, durationMs);
    return started;
  }

  private tone(
    notes: number[],
    duration: number,
    wave: OscillatorType,
    stagger: number,
  ): void {
    const context = this.context;
    const destination = this.sfxGain;
    if (!context || !destination) return;
    notes.forEach((frequency, index) => {
      if (this.voices >= 6) return;
      const start = context.currentTime + index * stagger;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = wave;
      oscillator.frequency.setValueAtTime(frequency, start);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.14, start + 0.008);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
      oscillator.connect(gain).connect(destination);
      this.voices += 1;
      oscillator.start(start);
      oscillator.stop(start + duration + 0.01);
      oscillator.addEventListener("ended", () => {
        this.voices = Math.max(0, this.voices - 1);
        oscillator.disconnect();
        gain.disconnect();
      });
    });
  }

  private startVictoryTheme(): void {
    const context = this.context;
    const destination = this.compressor;
    if (
      !context ||
      !destination ||
      this.muted ||
      this.lifecycleSuspended ||
      this.victoryThemeSource
    )
      return;
    this.victoryThemeBuffer ??= this.createVictoryThemeBuffer(context);
    const source = context.createBufferSource();
    const gain = context.createGain();
    const now = context.currentTime;
    source.buffer = this.victoryThemeBuffer;
    source.loop = true;
    source.loopEnd = this.victoryThemeBuffer.duration;
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(VICTORY_THEME_VOLUME, now + 0.6);
    source.connect(gain).connect(destination);
    this.victoryThemeSource = source;
    this.victoryThemeGain = gain;
    source.start(now);
    source.addEventListener("ended", () => {
      source.disconnect();
      gain.disconnect();
      if (this.victoryThemeSource === source) {
        this.victoryThemeSource = null;
        this.victoryThemeGain = null;
      }
    });
  }

  private stopVictoryTheme(): void {
    const context = this.context;
    const source = this.victoryThemeSource;
    const gain = this.victoryThemeGain;
    if (!context || !source || !gain) return;
    this.victoryThemeSource = null;
    this.victoryThemeGain = null;
    const now = context.currentTime;
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(Math.max(0.0001, gain.gain.value), now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
    source.stop(now + 0.13);
  }

  private createVictoryThemeBuffer(context: AudioContext): AudioBuffer {
    const secondsPerBeat = 60 / VICTORY_THEME_BPM;
    const duration = VICTORY_THEME_BEATS * secondsPerBeat;
    const frameCount = Math.ceil(duration * context.sampleRate);
    const buffer = context.createBuffer(2, frameCount, context.sampleRate);
    const leftChannel = buffer.getChannelData(0);
    const rightChannel = buffer.getChannelData(1);

    const renderNote = (note: ThemeNote, bass: boolean): void => {
      const start = note.beat * secondsPerBeat;
      const noteDuration = note.beats * secondsPerBeat;
      const startFrame = Math.floor(start * context.sampleRate);
      const noteFrames = Math.floor(noteDuration * context.sampleRate);
      const attack = bass ? 0.018 : 0.025;
      const release = Math.min(bass ? 0.22 : 0.3, noteDuration * 0.55);
      const left = Math.sqrt((1 - note.pan) / 2);
      const right = Math.sqrt((1 + note.pan) / 2);

      for (let frame = 0; frame < noteFrames; frame += 1) {
        const targetFrame = startFrame + frame;
        if (targetFrame >= frameCount) break;
        const time = frame / context.sampleRate;
        const attackEnvelope = Math.min(1, time / attack);
        const releaseEnvelope = Math.min(1, (noteDuration - time) / release);
        const envelope =
          attackEnvelope * Math.max(0, releaseEnvelope) * note.level;
        const phase = Math.PI * 2 * note.frequency * time;
        const sample = bass
          ? (Math.sin(phase) + Math.sin(phase * 2) * 0.18) * envelope
          : (Math.sin(phase) * 0.72 +
              Math.sin(phase * 2) * 0.2 +
              Math.sin(phase * 3) * 0.08) *
            envelope;
        leftChannel[targetFrame] =
          (leftChannel[targetFrame] ?? 0) + sample * left;
        rightChannel[targetFrame] =
          (rightChannel[targetFrame] ?? 0) + sample * right;
      }
    };

    VICTORY_THEME_LEAD.forEach((note) => renderNote(note, false));
    VICTORY_THEME_BASS.forEach((note) => renderNote(note, true));
    return buffer;
  }

  private async safePlay(audio: HTMLAudioElement): Promise<boolean> {
    if (!audio.src) return true;
    try {
      await audio.play();
      return true;
    } catch {
      // Playback support and autoplay policy vary; gameplay and Web Audio continue.
      return false;
    }
  }

  private async prepareMusic(): Promise<void> {
    if (this.musicPrepared) return;
    this.musicPrepared = true;
    try {
      const supportsOgg =
        this.menuMusic.canPlayType('audio/ogg; codecs="vorbis"') !== "";
      const extension = supportsOgg ? "ogg" : "mp3";
      const mimeType = supportsOgg ? "audio/ogg" : "audio/mpeg";
      const [menuUrl, gameUrl] = await Promise.all([
        this.loadBase64Audio(
          `audio/menumusicloop-tiggo.${extension}.b64`,
          mimeType,
        ),
        this.loadBase64Audio(
          `audio/levelmusicloop-tigrun.${extension}.b64`,
          mimeType,
        ),
      ]);
      this.menuMusic.src = menuUrl;
      this.gameMusic.src = gameUrl;
    } catch (error) {
      console.warn(
        "Music assets could not be prepared; procedural sound remains available.",
        error,
      );
    }
  }

  private async loadBase64Audio(
    relativePath: string,
    mimeType: string,
  ): Promise<string> {
    const response = await fetch(new URL(relativePath, document.baseURI));
    if (!response.ok)
      throw new Error(
        `Could not load ${relativePath}: HTTP ${response.status}`,
      );
    const encoded = (await response.text()).replace(/\s+/gu, "");
    const binary = atob(encoded);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1)
      bytes[index] = binary.charCodeAt(index);
    const url = URL.createObjectURL(new Blob([bytes], { type: mimeType }));
    this.musicObjectUrls.push(url);
    return url;
  }

  private fade(
    audio: HTMLAudioElement,
    target: number,
    durationMs: number,
  ): void {
    const version = (this.fadeVersions.get(audio) ?? 0) + 1;
    this.fadeVersions.set(audio, version);
    const start = audio.volume;
    const started = performance.now();
    const tick = (now: number): void => {
      if (
        this.fadeVersions.get(audio) !== version ||
        this.muted ||
        this.lifecycleSuspended
      )
        return;
      const progress = Math.max(0, Math.min(1, (now - started) / durationMs));
      audio.volume = Math.max(
        0,
        Math.min(1, start + (target - start) * progress),
      );
      if (progress < 1) requestAnimationFrame(tick);
      else if (target === 0) audio.pause();
    };
    requestAnimationFrame(tick);
  }

  private animateRate(
    audio: HTMLAudioElement,
    target: number,
    durationMs: number,
  ): void {
    const version = (this.rateVersions.get(audio) ?? 0) + 1;
    this.rateVersions.set(audio, version);
    const start = audio.playbackRate;
    const started = performance.now();
    const tick = (now: number): void => {
      if (this.rateVersions.get(audio) !== version) return;
      const progress = Math.max(0, Math.min(1, (now - started) / durationMs));
      audio.playbackRate = start + (target - start) * progress;
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  private cancelAnimations(): void {
    [this.menuMusic, this.gameMusic].forEach((audio) => {
      this.fadeVersions.set(audio, (this.fadeVersions.get(audio) ?? 0) + 1);
      this.rateVersions.set(audio, (this.rateVersions.get(audio) ?? 0) + 1);
    });
  }
}
